<template>
  <div>
    <v-layout mb-0 mt-2 align-center justify-center row wrap>      
      <div class="headline font-weight-regular">Rental Property Analysis</div>
      <v-flex xs12>

      <v-stepper v-model="el" alt-labels>
        <v-stepper-header>
          <v-stepper-step step="1" :complete="el > 1">Property Info</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="2" :complete="el > 2">Purchase Info</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="3" :complete="el > 3">Rental Info</v-stepper-step>
          <v-divider></v-divider>
          <v-stepper-step step="4" :complete="el > 4">Results</v-stepper-step>
        </v-stepper-header>

        <v-stepper-items>
          <v-stepper-content step="1">
            <v-form ref="formStep1" v-model="isStep1Valid">
              <v-card justify-start class="mb-2" color="white">
                <v-layout row wrap>
                <v-flex xs12>
                  <v-text-field v-model="reportName" ref="reportName" label="Report Name" box :rules="reportNameRules" @focus="$event.target.select()">
                    <v-tooltip top slot="append">
                      <v-icon color="primary" dark slot="activator">help</v-icon>
                      <span>This is the name by which you will remember the report.</span>
                    </v-tooltip>
                  </v-text-field>
                </v-flex>
                </v-layout>
                <v-layout row wrap>
                <v-flex xs12>
                  <v-text-field v-model="address" label="Property Address" box @focus="$event.target.select()"></v-text-field>
                </v-flex>
                </v-layout>
                <v-layout row>
                <v-flex d-flex xs12 md8>
                  <v-text-field v-model="city" label="City" box @focus="$event.target.select()"></v-text-field>
                </v-flex>
                <v-flex d-flex xs12 md2>
                  <v-text-field v-model="state" label="State" box @focus="$event.target.select()"></v-text-field>
                </v-flex>
                <v-flex d-flex xs12 md2>
                  <v-text-field v-model="zip" label="Zip" box @focus="$event.target.select()"></v-text-field>
                </v-flex>
                </v-layout>
                <v-layout row wrap>
                <v-flex xs12 sm6 md3>
                  <v-text-field v-model="propertyTax" label="Property Taxes" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                    <v-tooltip top slot="append">
                      <v-icon color="primary" dark slot="activator">help</v-icon>
                      <span>Annual Property Taxes.<br>If you don't know this, you can find<br>it on your county assessor's website.</span>
                    </v-tooltip>
                  </v-text-field>
                </v-flex>
                <v-flex xs12 sm6 md3>
                  <v-text-field v-model="mlsNumber" label="MLS Number" box @focus="$event.target.select()"></v-text-field>
                </v-flex>
                <v-flex xs12 sm12 md6>
                  <v-text-field v-model="source" label="Source" box @focus="$event.target.select()"></v-text-field>
                </v-flex>
                </v-layout>
                <v-layout row wrap>
                <v-flex xs12>
                  <v-textarea v-model="description" label="Property Description" auto-grow rows="1" box @focus="$event.target.select()"></v-textarea>
                </v-flex>
                </v-layout>
              </v-card>

              <v-layout align-center justify-end row fill-height>
                <v-btn to="/RentalCalcList" flat>Cancel</v-btn>
                <v-btn color="primary" @click="validateStep1">Next Step</v-btn>
              </v-layout>
            </v-form>
          </v-stepper-content>

          <v-stepper-content step="2">
            <v-form ref="formStep2" v-model="isStep2Valid">  
              <v-card justify-start class="mb-2" color="white">
                <v-layout row wrap>
                  <v-flex xs12 md4>
                    <div class="title font-weight-bold">Purchase Info</div>
                    <v-text-field v-model="purchasePrice" ref="purchasePrice" label="Purchase Price" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Total Price paid for the property</span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="afterRepairValue" label="After Repair Value" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>The expected value of the property after all repairs</span>
                      </v-tooltip>
                    </v-text-field>
                    <!-- <cost-summary v-model="repairCost" category="Repair" label="Repair Costs"></cost-summary> -->
                    <v-text-field v-model="repairCost" label="Repair Costs" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>All repair costs to get the property in a rentalable state</span>
                      </v-tooltip>
                    </v-text-field>
                  </v-flex>
                  <v-flex xs12 md4>
                    <div class="title font-weight-bold">Loan Details</div>
                    <v-text-field v-model="downPaymentPct" label="Down Payment" type="number" :rules="[rules.required, rules.percentage]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Percentage of total purchase price you will put down. Between .01 and 1</span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="interestRate" label="Interest Rate" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Interest Rate of the Loan. Value between .01 and 1</span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="term" label="Term Years" type="number" :rules="[rules.required, rules.term]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Total Years to pay back the Loan</span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="closingCost" label="Closing Costs" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Total Closing Costs associated with the Loan.<br>
                        Attorney Fees, Recording Fees, Prepaid taxes, etc.</span>
                      </v-tooltip>
                    </v-text-field>
                  </v-flex>
                </v-layout>

              </v-card>  
              
              <v-layout align-center justify-end row fill-height>
                <v-btn flat @click="el = 1">Back</v-btn>
                <v-btn color="primary" @click="validateStep2">Next Step</v-btn>
              </v-layout>
            </v-form>
          </v-stepper-content>

          <v-stepper-content name="3" step="3">
            <v-form ref="formStep3" v-model="isStep3Valid">  
              <v-card justify-start class="mb-2" color="white">
                <v-layout row wrap>
                  <v-flex xs12 md3>
                    <h3>Income</h3>
                    <!-- <cost-summary v-model="income" ref="income" category="Monthly Income" :rules="[rules.required]" label="Monthly Income"></cost-summary> -->
                    <v-text-field v-model="income" label="Monthly Income" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Include all sources of income, such as Rent, Laundry, Storage, etc.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                  </v-flex>
                  <v-flex xs12 md3>
                    <h3>Expenses</h3>
                    <!-- <cost-summary v-model="expenses" category="Monthly Expense" label="Monthly Expenses" @costs-updated="updateExpenseDetails"></cost-summary> -->
                    <v-text-field v-model="expenses" label="Monthly Expenses" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Monthly expenses you expect to incur, excluding loan costs, budgeted maintenance, repairs, etc.<br>
                        This should includes items like Insurance, HOA, Garbage, Electicity, Water, Parking, etc.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                  </v-flex>
                  <v-flex xs12 md3>
                    <h3>Budgeting Factors</h3>
                    <v-text-field v-model="factorVacancy" label="Vacancy" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>Monthly percentage you would like to budget for expected vacancy.</span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="factorCapEx" label="CapEx" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>
                          Monthly percentage you would like to budget for Capital Expenses.<br>
                          Ex - Roof, Flooring, Appliances, Mechanicals, etc.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="factorManagement" label="Management" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>
                          Monthly percentage you would like to budget for Management.<br>
                          Either self-managed or outsourced to a Property Management Company.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="factorRepairs" label="Repairs" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>
                          Monthly percentage you would like to budget for General Repairs.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                  </v-flex>
                  <v-flex xs12 md3>
                    <h3>Annual Projections</h3>
                    <v-text-field v-model="increaseIncome" label="Income Increase" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>
                          The percent by which you expect to increase rent each year you own the property.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="increaseExpense" label="Expense Increase" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>
                          The percent by which you expect expenses to increase each year you own the property.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                    <v-text-field v-model="increasePropertyValue" label="Property Value Increase" type="number" :rules="[rules.required]" box @focus="$event.target.select()">
                      <v-tooltip top slot="append">
                        <v-icon color="primary" dark slot="activator">help</v-icon>
                        <span>
                          The percent that you expect the value to increase each year you own the property.
                        </span>
                      </v-tooltip>
                    </v-text-field>
                  </v-flex>
                </v-layout>
              </v-card>
              <v-layout align-center justify-end row fill-height>
                <v-btn flat @click="el = 2">Back</v-btn>
                <v-btn color="primary" @click="validateStep3">Calculate</v-btn>
              </v-layout>
            </v-form>
          </v-stepper-content>

          <v-stepper-content name="4" step="4">
            <v-layout row wrap>
              <v-flex xs12 md6>
                <div class="title">
                    {{ address }}, {{ city }}, {{ state }} {{ zip }}
                </div>
                <div class="subheading">
                  {{ description }}
                </div>
                <div v-if="source" class="subheader font-italic">
                  Source: {{ source }}
                </div>
              </v-flex>
              <v-flex xs12 md6>
                <div class="font-weight-bold">Actions</div>
                <v-tooltip bottom>
                  <v-btn @click="el = 3" small color="primary" slot="activator">Edit</v-btn>
                  <span>Make Changes</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-btn @click="SaveReport" small color="primary" slot="activator">Save</v-btn>
                  <span>Save this Report</span>
                </v-tooltip>
                <!-- <v-tooltip bottom>
                  <v-btn small color="primary" slot="activator">PDF</v-btn>
                  <span>Create a PDF</span> 
                </v-tooltip> -->
                <v-tooltip bottom>
                  <v-btn to="/RentalCalcList" small color="primary" slot="activator">Exit Report</v-btn>
                  <span>Exit This Report</span>
                </v-tooltip>
              </v-flex>
            </v-layout>
            <v-card justify-start class="mt-3 mb-2">
              <v-layout row wrap>
                <v-flex xs4 lg3 mr-4 text-sm-left>
                  <v-layout>
                    <v-flex class="grey--text">
                        Purchase Price
                    </v-flex>
                    <v-flex class="text-xs-right">{{purchasePrice | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        After Repair Value
                    </v-flex>
                    <v-flex class="text-xs-right">{{afterRepairValue | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Down Payment PCT
                    </v-flex>
                    <v-flex class="text-xs-right">{{downPaymentPct | pct(2)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Loan Term (years)
                    </v-flex>
                    <v-flex class="text-xs-right">{{term}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Interest Rate
                    </v-flex>
                    <v-flex class="text-xs-right">{{interestRate | pct(2)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Property Tax
                    </v-flex>
                    <v-flex class="text-xs-right">{{propertyTax | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Closing Costs
                    </v-flex>
                    <v-flex class="text-xs-right">{{closingCost | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Estimated Repairs
                    </v-flex>
                    <v-flex class="text-xs-right">{{repairCost | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Total Project Cost
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcTotalProjectCost | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Down Payment
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcDownPayment | usd(0)}}</v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Loan Amount
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcLoanAmount | usd(0)}}</v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                        Monthly P&I
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcPni | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Total Cash Needed
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcTotalCashNeeded | usd(0)}}</v-flex>
                  </v-layout>

                  <v-layout mt-4>
                    <v-flex class="subheading">Other Financials</v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Total Initial Equity
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcTotalInitialEquity | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Income-Expense Ratio
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcIncomeExpenseRatio | pct(2)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Gross Initial ROE
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcGrossInitialRoe | pct(2)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Gross Yield
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcGrossYield | pct(2)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Debt Service Ratio
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcDebtServiceRatio | round(2)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Gross Rent Multiplier
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcGrossRentMultiplier | round(2)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Break Even (Years)
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcBreakEvenYears | round(2)}}</v-flex> 
                  </v-layout>

                  <v-layout mt-4>
                    <v-flex class="subheading">50% Rule</v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Total Monthly Income
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcMonthlyIncome | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      50% for Expenses
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcMonthlyIncome/2 | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Monthly P&I
                    </v-flex>
                    <v-flex class="text-xs-right">{{calcPni | usd(0)}}</v-flex> 
                  </v-layout>
                  <v-layout>
                    <v-flex class="grey--text">
                      Monthly Cashflow 50% Rule
                    </v-flex>
                    <v-flex class="text-xs-right">{{(calcMonthlyIncome/2)-calcPni | usd(0)}}</v-flex> 
                  </v-layout>

                </v-flex>
                <v-flex xs7 lg8 text-sm-left>
                  <v-layout row>
                    <table width="100%">
                      <tr mb-3>
                        <td>
                          <v-input persistent-hint hint="MONTHLY INCOME">
                            {{ calcMonthlyIncome | usd(0) }}
                          </v-input>
                        </td>
                        <td>
                          <v-input persistent-hint hint="MONTHLY EXPENSES">
                            {{ calcMonthlyExpenses | usd(0) }}
                          </v-input>
                        </td>
                        <td>
                          <v-input persistent-hint hint="MONTHLY CASHFLOW">
                            {{ calcMonthlyCashflow | usd(0) }}
                          </v-input>
                        </td>
                        <td>
                          <v-input persistent-hint hint="PRO FORMA CAP RATE">
                            {{ (calcNetOperatingIncome/calcTotalProjectCost) | pct(2) }}
                          </v-input>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <v-input persistent-hint hint="NET OPERATING INCOME">
                            {{ calcNetOperatingIncome | usd(0) }}
                          </v-input>
                        </td>
                        <td>
                          <v-input persistent-hint hint="TOTAL CASH NEEDED">
                            {{ calcTotalCashNeeded | usd(0) }}
                          </v-input>
                        </td>
                        <td>
                          <v-input persistent-hint hint="CASH ON CASH RETURN">
                            {{ calcCashOnCashReturn | pct(2)}}
                          </v-input>
                        </td>
                        <td>
                          <v-input persistent-hint hint="PURCHASE CAP RATE">
                            {{ (calcNetOperatingIncome/this.purchasePrice) | pct(2) }}
                          </v-input>
                        </td>
                      </tr>
                    </table>
                  </v-layout>
                  <v-layout row>
                    <v-flex xs12>
                      <GChart
                        :type="expenseChartType"
                        :data="expenseChartData"
                        :options="expenseChartOptions"
                      />
                    </v-flex>
                  </v-layout>
                  <v-layout>
                    <v-flex xs12>
                      <GChart
                        :type="trendChartType"
                        :data="trendChartData"
                        :options="trendChartOptions"                        
                      />
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 pa-3>
                  <v-divider></v-divider>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12>
                  <b>Analysis Over Time</b>
                  <v-data-table
                    :headers="projectionHeaders"
                    :items=calcProjectedValuesFormatted
                    hide-actions
                    class="mytable"
                  >
                    <template slot="items" slot-scope="props">
                      <td>{{ props.item.variable }}</td>
                      <td class="text-xs-right">{{ props.item.y1 }}</td>
                      <td class="text-xs-right">{{ props.item.y2 }}</td>
                      <td class="text-xs-right">{{ props.item.y3 }}</td>
                      <td class="text-xs-right">{{ props.item.y4 }}</td>
                      <td class="text-xs-right">{{ props.item.y5 }}</td>
                      <td class="text-xs-right">{{ props.item.y10 }}</td>
                      <td class="text-xs-right">{{ props.item.y15 }}</td>
                      <td class="text-xs-right">{{ props.item.y20 }}</td>
                      <td class="text-xs-right">{{ props.item.y30 }}</td>
                    </template>
                  </v-data-table>
                </v-flex>
              </v-layout>
            </v-card>
          </v-stepper-content>
        </v-stepper-items>
      </v-stepper>

      <v-card flat dark tile class="secondary text-xs-center">
          <v-card-text class="grey--text text--darken-3">Notice any errors or mistakes? Send us an email</v-card-text>
          <v-divider></v-divider>
          <v-card-text class="grey--text">
          The calculators found on RentalAware are designed to be used for informational and educational purposes only, and when used alone, do not constitute investment advice. 
          RentalAware recommends that you seek the advice of a real estate professional before making any type of investment. 
          The results presented may not reflect the actual return of your own investments. 
          RentalAware is not responsible for the consequences of any decisions or actions taken in reliance upon or as a result of the information provided by these tools. 
          Furthermore, RentalAware is not responsible for any human or mechanical errors or omissions.
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="alert" max-width="300">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          Rental Analysis
        </v-card-title>
        <v-card-text>
          Save was successful. Click OK to proceed.
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" flat @click="alert = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import db from '../../firebaseInit';
import { GChart } from 'vue-google-charts'

export default {
    name: 'rental',    
    components: {
      GChart
    },
    data () {
      return {
        alert: false,
        el: 3,
        isStep1Valid: true,
        reportName: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        propertyTax: 0,
        mlsNumber: '',
        source: '',
        description: '',
        reportNameRules: [
          v => !!v || 'Name is required',
          v => (v && v.length <= 100) || 'Name must be less than 10 characters'
        ],
        rules: {
          required: value => !!value || 'Required.',
          counter: value => value.length <= 20 || 'Max 20 characters',
          email: value => {
            const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            return pattern.test(value) || 'Invalid e-mail.'
          },
          percentage: value => value >= 0 && value <= 1 || 'Percentage between 0 and 1',
          term: value => value > 0 && value <= 30 || 'Term between 1 and 30',
          greaterThanZero: value => value > 0
        },
        isStep2Valid: true,
        purchasePrice: 0,
        afterRepairValue: 0,
        closingCost: 0,
        repairCost: 0,
        downPaymentPct: .20,
        interestRate: .0425,
        term: 30,
        isStep3Valid: true,
        income: 0,
        factorVacancy: .05,
        factorCapEx: .10,
        factorManagement: .10,
        factorRepairs: .10,
        increaseIncome: .01,
        increaseExpense: .01,
        increasePropertyValue: .01,
        expenseChartType: 'PieChart',
        expenses: 0,
        expenseList: [], 
        expenseChartData: [],   
        expenseChartOptions: {'title':'Monthly Expenses',
                        'width':800,
                        'height':325,
                        'tooltip.ignoreBounds':false
        },
        trendChartType: "LineChart",
        trendChartOptions: {
          title: 'Trend Analysis',
          'width':800,
          'height':325
        },
        trendChartData: [],
        projectionHeaders: [
          { text: 'Year', value: 'variable', align: 'left', sortable: false },
          { text: '1', value: 'y1', align: 'right', sortable: false },
          { text: '2', value: 'y2', align: 'right', sortable: false  },
          { text: '3', value: 'y3', align: 'right', sortable: false  },
          { text: '4', value: 'y4', align: 'right', sortable: false  },
          { text: '5', value: 'y5', align: 'right', sortable: false  },
          { text: '10', value: 'y10', align: 'right', sortable: false  },
          { text: '15', value: 'y15', align: 'right', sortable: false  },
          { text: '20', value: 'y20', align: 'right', sortable: false  },
          { text: '30', value: 'y30', align: 'right', sortable: false  }
        ]
      }
    },
    methods: {
      validateStep1 () {
        if (this.$refs.formStep1.validate()) {
          this.isStep1Valid = true;
          this.el = 2;
          this.$refs.purchasePrice.focus();
        }
      },
      validateStep2(){
        if (this.$refs.formStep2.validate()) {
          this.isStep2Valid = true;
          this.el = 3;
        }
      },
      validateStep3(){
        if (this.$refs.formStep3.validate()){
          this.ShowCalculationStep();
          this.el = 4;
          this.isStep3Valid = true;
        }
      },
      updateExpenseDetails(e){
        this.expenseList = [];
        for (var x = 0; x < e.length; x++) {
          this.expenseList.push([ e[x].costName, parseFloat(e[x].cost) ]);
        }
      },
      FV(PV,rate,n){
        var x=(1+rate)
        var fv=PV*(Math.pow(x,n))
        return fv;
      },
      IPMT(PV,rate,per,nper){
        //https://community.qlik.com/t5/QlikView-App-Development/How-to-calculate-IPMT-in-QV/td-p/833867
        //pv*rt*(Pow(rt+1,Tn+1)-Pow(rt+1,n)) / ((rt+1)*(Pow(rt+1,Tn)-1))
        if (PV==0 || PV==null) return 0;
        if (rate==0 || rate==null) return 0;
        if (per==0 || per==null) return 0;
        if (nper==0 || nper==null) return 0;

        var ipmt = PV * rate * (Math.pow(rate+1,nper+1)-Math.pow(rate+1,per)) / ((rate+1) * (Math.pow(rate+1,nper)-1));
        return ipmt;
      },
      PMT(PV,rate,nper){
        if (PV==0 || PV==null) return 0;
        if (rate==0 || rate==null) return 0;
        if (nper==0 || nper==null) return 0;

        var pmt = - rate * PV * (Math.pow(1 + rate, nper) + 0) / (Math.pow(1 + rate, nper) - 1);
        return pmt;
      },
      PPMT(pmt, ipmt){
        return pmt - ipmt;
      },
      LoanBalance(pmt, ipmt, rate){
        return ipmt/rate-pmt;
      },
      TotalAnnualCashflow(income, expenses){
        return income - expenses;
      },
      FormatValue(value, format){
        if (value==0 || !value) return 0;
        if (format=='' || !format) return 0;

        if (format==='currency')
          return this.$options.filters.usd(value, 2);
        
        if (format==="percentage")
          return this.$options.filters.pct(value, 2);

        return 0;
      },
      ShowCalculationStep(){
        this.SetTrendChartData();
        
        //re-render with chart type
        this.$nextTick(() => {
            this.trendChartType = 'AreaChart';
            this.SetExpenseChartData(); //due to calculated properties
            this.expenseChartType = "PieChart";
        });
      },
      SaveReport(){
        var that = this; //preserve reference before promise call

        // Add a new document with a generated id.
        db.collection("rentAnalysis").add({
            userName: this.userName,
            timeStamp: new Date(),
            reportName: this.reportName,
            description: this.description,
            address: this.address,
            city: this.city,
            state: this.state,
            zip: this.zip,
            propertyTax: this.propertyTax,
            mls: "",
            source: this.source,
            purchasePrice: this.purchasePrice,
            afterRepairValue: this.afterRepairValue,
            repairCost: this.repairCost,
            downPaymentPct: this.downPaymentPct,
            interestRate: this.interestRate,
            term: this.term,
            closingCost: this.closingCost,
            income: this.income,
            expenses: this.expenses,
            factorVacancy: this.factorVacancy,
            factorCapEx: this.factorCapEx,
            factorManagement: this.factorManagement,
            factorRepairs: this.factorRepairs,
            increaseIncome: this.increaseIncome,
            increaseExpense: this.increaseExpense,
            increasePropertyValue: this.increasePropertyValue
        })
        .then(function(docRef) {
            console.log("Document written with ID: ", docRef.id);
            that.alert = true;
        })
        .catch(function(error) {
            console.error("Error adding document: ", error);
        });
      },
      NewReport(){
        this.el = 1;
        this.reportName = '';
        this.address = '';
        this.city = '';
        this.state = '';
        this.zip = '';
        this.propertyTax = 0;
        this.mlsNumber = '';
        this.source = '';
        this.description = '';
        this.purchasePrice = 0;
        this.afterRepairValue = 0;
        this.closingCost = 2000;
        this.repairCost = 0;
        this.downPaymentPct = .25;
        this.interestRate = .0425;
        this.term = 30;
        this.income = 0;
        this.factorVacancy = .05;
        this.factorCapEx = .10;
        this.factorManagement = .10;
        this.factorRepairs = .10;
        this.increaseIncome = .01;
        this.increaseExpense = .01;
        this.increasePropertyValue = .01;
        this.expenses = 0;
      },
      LoadReport(){

        // db.collection("rentAnalysis").where("address", "==", "902 Washington Street").get().then((querySnapshot) => {
        //   querySnapshot.forEach((doc) => {
        //     this.reportName = doc.data().reportName;
        //   });  
        // });
        
        db.collection("rentAnalysis").doc(this.reportId).get().then((doc) => {
          //console.log(doc.id);
          this.el = 1;
          this.reportName = doc.data().reportName;
          this.address = doc.data().address;
          this.city = doc.data().city;
          this.state = doc.data().state;
          this.zip = doc.data().zip;
          this.propertyTax = doc.data().propertyTax;
          this.mlsNumber = doc.data().mlsNumber;
          this.source = doc.data().source;
          this.description = doc.data().description;
          this.purchasePrice = doc.data().purchasePrice;
          this.afterRepairValue = doc.data().afterRepairValue;
          this.closingCost = doc.data().closingCost;
          this.repairCost = doc.data().repairCost;
          this.downPaymentPct = doc.data().downPaymentPct;
          this.interestRate = doc.data().interestRate;
          this.term = doc.data().term;
          this.income = doc.data().income;
          this.factorVacancy = doc.data().factorVacancy;
          this.factorCapEx = doc.data().factorCapEx;
          this.factorManagement = doc.data().factorManagement;
          this.factorRepairs = doc.data().factorRepairs;
          this.increaseIncome = doc.data().increaseIncome;
          this.increaseExpense = doc.data().increaseExpense;
          this.increasePropertyValue = doc.data().increasePropertyValue;
          this.expenses = doc.data().expenses;
        });
        
        //docRef.get().then(function(doc) {
          //if (doc.exists) {
          //  this.BindDocData(doc.data());
          //}
          //else {
          //  console.log("Document does not exist!");
          //}
        //}).catch(function(error) {
        //    console.log("Error getting document:", error);
        //});
      },
      SetTrendChartData(){
        var calcs = this.calcProjectedValuesRaw;
        var o = [];
    
        o.push(['Year', 'Balance', 'Equity', 'Value']);
        //get loan balance from calculation array
        o.push(['1', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y1,
                      calcs.filter(x => x.variable==='Equity')[0].y1, 
                      calcs.filter(x => x.variable==='Property Value')[0].y1]);
        o.push(['2', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y2,
                      calcs.filter(x => x.variable==='Equity')[0].y2, 
                      calcs.filter(x => x.variable==='Property Value')[0].y2]);
        o.push(['3', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y3,
                      calcs.filter(x => x.variable==='Equity')[0].y3, 
                      calcs.filter(x => x.variable==='Property Value')[0].y3]);
        o.push(['4', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y4,
                      calcs.filter(x => x.variable==='Equity')[0].y4, 
                      calcs.filter(x => x.variable==='Property Value')[0].y4]);
        o.push(['5', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y5,
                      calcs.filter(x => x.variable==='Equity')[0].y5, 
                      calcs.filter(x => x.variable==='Property Value')[0].y5]);
        o.push(['10', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y10,
                      calcs.filter(x => x.variable==='Equity')[0].y10, 
                      calcs.filter(x => x.variable==='Property Value')[0].y10]);
        o.push(['15', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y15,
                      calcs.filter(x => x.variable==='Equity')[0].y15, 
                      calcs.filter(x => x.variable==='Property Value')[0].y15]);
        o.push(['20', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y20,
                      calcs.filter(x => x.variable==='Equity')[0].y20, 
                      calcs.filter(x => x.variable==='Property Value')[0].y20]);
        o.push(['30', 
                      calcs.filter(x => x.variable==='Loan Balance')[0].y30,
                      calcs.filter(x => x.variable==='Equity')[0].y30, 
                      calcs.filter(x => x.variable==='Property Value')[0].y30]);
        //console.log(o);
        this.trendChartData = o;  
      },
      SetExpenseChartData(){
        var ecd = [];
        ecd.push(['Category', 'Cost']);
        ecd.push(['P&I', this.calcPni]);
        ecd.push(['Vacancy', this.calcVacancy]);
        ecd.push(['Management', this.calcManagement]);
        ecd.push(['Repairs', this.calcRepairs]);
        ecd.push(['CapEx', this.calcCapEx]);
        ecd.push(['Property Tax', this.calcPropertyTax]);
        
        if (this.expenseList.length > 0)
          ecd.concat(this.expenseList);
        else
        {
          //nothing added to details array, so return user entry
          ecd.push(["Other", parseFloat(this.expenses)]);
        }
        this.expenseChartData = ecd;
      }
    },
    computed: {
      reportId(){
        return this.$store.state.selectedRentalCalcId;
      },
      userName(){
        return this.$store.state.user.email;
      },
      calcMonthlyIncome(){
        return this.income;
      },
      calcMonthlyCashflow(){
        return this.calcMonthlyIncome - this.calcMonthlyExpenses;
      },
      calcVacancy(){
        return this.income * this.factorVacancy;
      },
      calcManagement(){
        return this.income * this.factorManagement;
      },
      calcCapEx(){
        return this.income * this.factorCapEx;
      },
      calcRepairs(){
        return this.income * this.factorRepairs;
      },
      calcPropertyTax(){
        return this.propertyTax/12;
      },
      calcTotalProjectCost(){
        return parseFloat(this.purchasePrice) + parseFloat(this.closingCost) + parseFloat(this.repairCost);
      },
      calcDownPayment(){
        return this.purchasePrice * this.downPaymentPct;
      },
      calcLoanAmount(){
        return this.purchasePrice - this.calcDownPayment;
      },
      calcPni(){
        var interest = this.interestRate / 12;
        var totalPayments = this.term * 12;
        var principal = this.purchasePrice - this.calcDownPayment;

        var M = principal * interest * (Math.pow(1+interest, totalPayments)) / (Math.pow(1+interest, totalPayments) - 1);
        return M;

      },
      calcTotalCashNeeded(){
        return parseFloat(this.closingCost) + parseFloat(this.repairCost) + parseFloat(this.calcDownPayment);
      },
      calcCashOnCashReturn(){
        return (this.calcMonthlyCashflow * 12) / this.calcTotalCashNeeded;
      },
      calcNetOperatingIncome(){
        //noi should not include pni
        return (this.calcMonthlyIncome - this.calcMonthlyExpenses + this.calcPni) * 12;
      },
      calcIncomeExpenseRatio(){
        return this.calcMonthlyIncome / this.purchasePrice;
      },
      calcGrossYield(){
        return (this.calcMonthlyIncome * 12) / this.purchasePrice;
      },
      calcDebtServiceRatio(){
        return this.calcNetOperatingIncome / (this.calcPni * 12);
      },
      calcTotalInitialEquity(){
        return this.afterRepairValue - this.calcLoanAmount;
      },
      calcGrossRentMultiplier(){
        return this.purchasePrice / (this.calcMonthlyIncome * 12);
      },
      calcGrossInitialRoe(){
        return (this.calcMonthlyCashflow * 12) / this.calcTotalInitialEquity;
      },
      calcBreakEvenYears(){
        return this.calcTotalCashNeeded / (this.calcMonthlyCashflow * 12);
      },
      calcMonthlyExpenses(){
        var totalExpenses = 0;
        var arr = this.expenseChartData;
        for(var i=1; i<arr.length; i++){
          totalExpenses += arr[i][1];
        }
        return totalExpenses;
      },
      calcMonthlyOperatingExpenses(){
        return this.calcMonthlyExpenses - this.calcPni;
      },
      calcProjectedValuesRaw(){
        var output = [];

        var totalAnnIncome = {variable:'Total Annual Income',
                    type: 'currency',
                    show: 1,
                    y1:this.calcMonthlyIncome*12,
                    y2:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,1),
                    y3:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,2),
                    y4:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,3),
                    y5:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,4),
                    y10:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,9),
                    y15:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,14),
                    y20:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,19),
                    y30:this.FV((this.calcMonthlyIncome*12),this.increaseIncome,29)
                  };
        output.push(totalAnnIncome);

        var totAnnExpenses = {variable:'Total Annual Expenses',
                    type: 'currency',
                    show: 1,
                    y1:this.calcMonthlyExpenses*12,
                    y2:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,2)+(this.calcPni*12),
                    y3:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,3)+(this.calcPni*12),
                    y4:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,4)+(this.calcPni*12),
                    y5:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,5)+(this.calcPni*12),
                    y10:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,10)+(this.calcPni*12),
                    y15:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,15)+(this.calcPni*12),
                    y20:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,20)+(this.calcPni*12),
                    y30:this.FV((this.calcMonthlyOperatingExpenses*12),this.increaseExpense,30)
                  };
        output.push(totAnnExpenses);
        
        var tac = { variable:'Total Annual Cashflow',
                    type: 'currency',
                    show: 1,
                    y1:this.TotalAnnualCashflow(totalAnnIncome.y1,totAnnExpenses.y1),
                    y2:this.TotalAnnualCashflow(totalAnnIncome.y2,totAnnExpenses.y2),
                    y3:this.TotalAnnualCashflow(totalAnnIncome.y3,totAnnExpenses.y3),
                    y4:this.TotalAnnualCashflow(totalAnnIncome.y4,totAnnExpenses.y4),
                    y5:this.TotalAnnualCashflow(totalAnnIncome.y5,totAnnExpenses.y5),
                    y10:this.TotalAnnualCashflow(totalAnnIncome.y10,totAnnExpenses.y10),
                    y15:this.TotalAnnualCashflow(totalAnnIncome.y15,totAnnExpenses.y15),
                    y20:this.TotalAnnualCashflow(totalAnnIncome.y20,totAnnExpenses.y20),
                    y30:this.TotalAnnualCashflow(totalAnnIncome.y30,totAnnExpenses.y30)
                  };
        output.push(tac);
        
        var cacroi = {variable:'Cash on Cash ROI',
                        type: 'percentage',
                        show: 1,
                        y1:(tac.y1/this.calcTotalCashNeeded),
                        y2:(tac.y2/this.calcTotalCashNeeded),
                        y3:(tac.y3/this.calcTotalCashNeeded),
                        y4:(tac.y4/this.calcTotalCashNeeded),
                        y5:(tac.y5/this.calcTotalCashNeeded),
                        y10:(tac.y10/this.calcTotalCashNeeded),
                        y15:(tac.y15/this.calcTotalCashNeeded),
                        y20:(tac.y20/this.calcTotalCashNeeded),
                        y30:(tac.y30/this.calcTotalCashNeeded)
                    };
        output.push(cacroi);

        var payment = {variable:'Payment',
                        type: 'currency',
                        show: 0,
                        y1:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y2:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y3:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y4:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y5:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y10:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y15:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y20:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12),
                        y30:this.PMT(-this.calcLoanAmount, (this.interestRate/12), this.term*12)
                      };
        output.push(payment);

        //IPMT(PV,rate,per,nper)
        var ipmt = {variable:'Interest',
                        type: 'currency',
                        show: 0,
                        y1:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 12, this.term*12),
                        y2:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 24, this.term*12),
                        y3:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 36, this.term*12),
                        y4:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 48, this.term*12),
                        y5:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 60, this.term*12),
                        y10:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 120, this.term*12),
                        y15:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 180, this.term*12),
                        y20:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 240, this.term*12),
                        y30:this.IPMT(this.calcLoanAmount, (this.interestRate/12), 360, this.term*12)
                      };
        output.push(ipmt);

        var ppmt = {variable:'Principle',
                        type: 'currency',
                        show: 0,
                        y1:this.PPMT(payment.y1, ipmt.y1),
                        y2:this.PPMT(payment.y2, ipmt.y2),
                        y3:this.PPMT(payment.y3, ipmt.y3),
                        y4:this.PPMT(payment.y4, ipmt.y4),
                        y5:this.PPMT(payment.y5, ipmt.y5),
                        y10:this.PPMT(payment.y10, ipmt.y10),
                        y15:this.PPMT(payment.y15, ipmt.y15),
                        y20:this.PPMT(payment.y20, ipmt.y20),
                        y30:this.PPMT(payment.y30, ipmt.y30)
                      };
        output.push(ppmt);

        var bal = {variable:'Loan Balance',
                        type: 'currency',
                        show: 1,
                        y1:this.LoanBalance(ppmt.y1, ipmt.y1, (this.interestRate/12)),
                        y2:this.LoanBalance(ppmt.y2, ipmt.y2, (this.interestRate/12)),
                        y3:this.LoanBalance(ppmt.y3, ipmt.y3, (this.interestRate/12)),
                        y4:this.LoanBalance(ppmt.y4, ipmt.y4, (this.interestRate/12)),
                        y5:this.LoanBalance(ppmt.y5, ipmt.y5, (this.interestRate/12)),
                        y10:this.LoanBalance(ppmt.y10, ipmt.y10, (this.interestRate/12)),
                        y15:this.LoanBalance(ppmt.y15, ipmt.y15, (this.interestRate/12)),
                        y20:this.LoanBalance(ppmt.y20, ipmt.y20, (this.interestRate/12)),
                        y30:0
                      }
        output.push(bal);

        var propval = {variable:'Property Value',
                        type: 'currency',
                        show: 1,
                        y1:(this.afterRepairValue * (1+this.increasePropertyValue)),
                        y2:(this.FV(this.afterRepairValue,this.increasePropertyValue,2)),
                        y3:(this.FV(this.afterRepairValue,this.increasePropertyValue,3)),
                        y4:(this.FV(this.afterRepairValue,this.increasePropertyValue,4)),
                        y5:(this.FV(this.afterRepairValue,this.increasePropertyValue,5)),
                        y10:(this.FV(this.afterRepairValue,this.increasePropertyValue,10)),
                        y15:(this.FV(this.afterRepairValue,this.increasePropertyValue,15)),
                        y20:(this.FV(this.afterRepairValue,this.increasePropertyValue,20)),
                        y30:(this.FV(this.afterRepairValue,this.increasePropertyValue,30))
                      };
        output.push(propval);

        var equity = {variable:'Equity',
                        type: 'currency',
                        show: 1,
                        y1:propval.y1-bal.y1,
                        y2:propval.y2-bal.y2,
                        y3:propval.y3-bal.y3,
                        y4:propval.y4-bal.y4,
                        y5:propval.y5-bal.y5,
                        y10:propval.y10-bal.y10,
                        y15:propval.y15-bal.y15,
                        y20:propval.y20-bal.y20,
                        y30:propval.y30-bal.y30
                      }
        output.push(equity);

        return output;
      },
      calcProjectedValuesFormatted(){
        var rawValues = this.calcProjectedValuesRaw;
        var formattedValues = [];
        for (var i=0; i<rawValues.length; i++){
          if (rawValues[i].show==1){
            formattedValues.push(
              {variable:rawValues[i].variable,
                y1:this.FormatValue(rawValues[i].y1, rawValues[i].type),
                y2:this.FormatValue(rawValues[i].y2, rawValues[i].type),
                y3:this.FormatValue(rawValues[i].y3, rawValues[i].type),
                y4:this.FormatValue(rawValues[i].y4, rawValues[i].type),
                y5:this.FormatValue(rawValues[i].y5, rawValues[i].type),
                y10:this.FormatValue(rawValues[i].y10, rawValues[i].type),
                y15:this.FormatValue(rawValues[i].y15, rawValues[i].type),
                y20:this.FormatValue(rawValues[i].y20, rawValues[i].type),
                y30:this.FormatValue(rawValues[i].y30, rawValues[i].type)
              }
            );
          }
        }
        return formattedValues;
      }
    },
    created: function(){
      this.NewReport();
      if (this.reportId != '') {
        this.LoadReport();
      }
    }
  }
</script>

<style scoped>
  label {
    display: inline-block;
    width: 140px;
    text-align: left;
  }
  .spanControl {
    display: inline-block;
    width: 500px;
    background-color: red;
  }
  table {
    padding: 0 2em 1em 0;
  }
  td {
    padding-bottom: 10px;
  }
  v-table {
      border-bottom: 10px;
      background-color: red;
  }
</style>